import { useEffect, useMemo, useRef, useState } from "react";
import { ChangeAlertCallbackType } from "../../../Root";
import { SpaceService } from "../../../../services/SpaceService";
import { CommonService } from "../../../../services/CommonService";
import { MenuItem, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";

export const useSpaceCancel = (changeAlert: ChangeAlertCallbackType) => {
    const [list, setList] = useState([]);
    const [type, setType] = useState("");
    const [catalog, setCatalog] = useState(Array<{ v: ""; k: "" }>);
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [disabledAdd, setDisabledAdd] = useState(false);

    const commonService = useRef<CommonService>();
    const spaceService = useRef<SpaceService>();

    const init = async () => {
        setList([]);
        setDisabled(true);
    };

    const getCatalog = async (type: string) => {
        setLoading(true);
        setType(type);

        let data = {
            catalog: {
                id: "cancelation.type",
                filters:
                    type === "cancel" ? 
					[{ type: "cancel", "metadata.requester": "compliance" }]
                    : [{ type: "block" }],
            },
        };

        const response = await commonService.current.getCatalog(data);
        if (response?.data?.response === "OK") {
            setCatalog(response.data.catalog);
            setLoading(false);
        } else {
            setLoading(false);
            changeAlert(true, "error", "Ocurrió un error, " + response.data.message);
        }
    };

    const onSelectFile = async (e) => {
        let csv = e.target.files[0];
        if (!csv) {
            changeAlert(true, "error", "Error en selección de archivo");
        } else if (csv.type !== "text/csv") {
            changeAlert(true, "error", "Error: El tipo de archivo permitido es CSV");
        } else {
            let reader = new FileReader();

            if (csv) {
                reader.readAsDataURL(csv);
                reader.onload = ((binding) => {
                    return async () => {
                        setLoading(true);
                        let parts = reader.result.toString().split(",")[1];
                        let props = {
                            uuid: "UUID",
                            clabe: "Clabe",
                            email: "email_cliente",
                            reason: "reason",
                            comment: "TAG",
                        };
                        const response = await commonService.current.csvToJson(parts, props, "space");
                        if (response?.data?.response === "OK") {
                            let content = response.data.content;
                            let total = list.concat(content);
                            let data = Object.values(
                                total.reduce(
                                    (acc, cur) =>
                                        Object.assign(acc, {
                                            [cur.customer.uuid]: cur,
                                        }),
                                    {}
                                )
                            );

                            validate(data, "");
                            setList(data);
                            setLoading(false);
                        } else {
                            setList([]);
                            setLoading(false);
                            changeAlert(true, "error", response.data.message);
                        }
                    };
                })(this);
            }
        }
    };

    const deleteCustomer = async (uuid) => {
        const cleanList = list.filter((item) => {
            return item.customer.uuid !== uuid;
        });
        if (cleanList.length < 50) {
            setDisabledAdd(false);
        } else {
            setDisabledAdd(true);
        }
        validate(cleanList, "");
        setList(JSON.parse(JSON.stringify(cleanList)));
    };

    const onSelectReason = (event, uuid) => {
        let value = event.target.value as string;
        let err = "";
        const data = list.map((item) => {
            if (item.customer.uuid === uuid) {
                item["reason_text"] = catalog.filter((c) => c["k"] === value)[0]["v"]
                item.reason = Number(value);
            }
            return item;
        });

        if (Number(value) > -1) {
            err = "";
        } else {
            err = "Dato obligatorio";
        }

        validate(data, err);
        setList(data);
    };

    const addComment = (event, uuid) => {
        let value = event.target.value as string;
        const data = list.map((item) => {
            if (item.customer.uuid === uuid) {
                item.comment = value;
            }
            return item;
        });
        validate(data, "");
        setList(data);
    };

    const validate = async (f, er) => {
        for (let i = 0; i < f.length; i++) {
            if (f[i]["customer"]["uuid"] && f[i]["reason"] && f[i]["comment"] && !er) {
                setDisabled(false);
            } else {
                setDisabled(true);
                break;
            }
        }
        if (f.length === 0) {
            setDisabled(true);
        }
    };

    const send = async () => {
        setLoading(true);
        let data = []
        let user = JSON.parse(localStorage.session);
        let role = localStorage.config && JSON.parse(localStorage.config)["idRol"] ? JSON.parse(localStorage.config)["idRol"] : "";

        list.map((item) => {
            return data.push({
                space: {
                    uuid: item.spaceUuid,
                    owner: {
                        application_uuid: "albo.os",
                        entity_uuid: item.customer.uuid,
                    },
                },
                customerUuid: item.customer.uuid,
                reason: item.reason,
                clabe: item.clabe,
                extra: {
                    comment: item.comment,
                    reason: item.reason_text,
                },
                agent_info: {
                    name: user["name"],
                    email: user["email"],
                    rol: role
                }
            })

        })

        const response = await spaceService.current[type === "cancel" ? "spaceCancel" : "spaceBlock"](data);
        if (response?.data?.response === "OK") {
            await init();
            changeAlert(true, "success", response.data.message);
            setLoading(false);
        } else {
            setLoading(false);
            changeAlert(true, "error", response.data.message);
        }
    };

    useEffect(() => {
        spaceService.current = new SpaceService(changeAlert);
        commonService.current = new CommonService(changeAlert);
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calculateColumns = useMemo(() => {
        const columns = [
            { cell: (row) => {
				return <Close titleAccess={`Eliminar de \nla lista`} onClick={() => deleteCustomer(row.customer.uuid)} cursor={"pointer"} fontSize="small"/>
			}, sortable: true, width: "2%"},
            { selector: (row) => row.customer.name, name: "Nombre", wrap: true, sortable: true, width: "15%"},
            { selector: (row) => row.email, name: "Email", wrap: true, sortable: true, width: "15%" },
            { selector: (row) => row.clabe, name: "CLABE", wrap: true, sortable: true, width: "16%" },
            { cell: (row) => {
                    return (
                        <TextField value={row.reason || -1} onChange={(e) => {onSelectReason(e, row.customer.uuid)}} select size="small" fullWidth color="secondary">
                            <MenuItem value={-1} disabled>Selecione</MenuItem>
                            {catalog?.map((item, index) => (
                                <MenuItem key={`cancel-option-${index}`} value={item["k"]}>
                                    {item["v"]}
                                </MenuItem>
                            ))}
                        </TextField>
                    );
                }, name: "Motivo", sortable: true, width: "19%"
            },
            { cell: (row) => {
			 	return <TextField multiline maxRows={2} value={row.comment || ""} onChange={(e) => {addComment(e, row.customer.uuid)}} size="small" fullWidth color="secondary" sx={{py: 1}}/>
			}, name: "Comentarios", sortable: true,
            },
        ];
        return columns;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);


    return {
        list,
		loading,
        disabled,
        disabledAdd,
        columns: calculateColumns,
        getCatalog,
        onSelectFile,
        send,
    };
};
